// Einstufung v2 (Entscheidung 34): Wo fängt jemand im Kurs an? Reine Funktionen ohne Oberfläche, Speicher und Uhr (test/placement.test.js).
//
// Ablauf: Selbsteinschätzung mit Kann-Sätzen bestimmt nur, wo die Suche beginnt. Dann kurze Proben aus dem Kurs selbst: je Abschnitt 4 Sätze,
// zu jedem die passende deutsche Bedeutung aus 4 wählen oder „Weiß ich nicht“. Bestanden: 3 von 4; bei 2 von 4 kommen 2 Sätze dazu, dann 4 von 6.
// Binäre Suche über die Abschnitte, höchstens maxProbes(K) Proben. Ergebnis ist der erste nicht bestandene Abschnitt (im Zweifel der frühere).
// Alles davor gilt als vorausgesetzt: Es wird nicht neu eingeführt. Kurz geprüft (höchstens CHECKS_PER_DAY am Tag, vom Startpunkt rückwärts) werden
// nur die Lektionen direkt davor, zusammen mindestens CHECK_ZONE Wörter und Wendungen (checkFrom bis startPos − 1); frühere gelten ohne Prüfung als
// bekannt (Arne, 21.10.2026: Wer bei Lektion 95 startet, soll nicht mit „Buongiorno“ anfangen).
// Sprachneutral und für jede Deckgröße: Abschnitte, Aufgaben und Startpunkt entstehen aus dem Deck selbst (Positionen, Lektionen, Sätze mit
// deutscher Bedeutung). Wächst das Deck (B1, B2), wachsen Abschnittsgröße (höchstens MAX_SECTIONS Abschnitte) und Zahl der Proben (logarithmisch)
// mit; der Kann-Satz für Fortgeschrittene erscheint erst, wenn das Deck so weit reicht. test/placement.test.js prüft jedes Deck aus
// public/content/index.json und ein künstliches Deck mit 5.000 Wörtern.
// Planungswerte, nicht kalibriert: Abschnittsgröße, Bestehensgrenzen, Startpunkte der Kann-Sätze, Prüfbereich, Prüfungen je Tag.
// Kein Ja/Nein-Wortschatztest mehr und keine Zielfrage (Arne, 21.10.2026).

export const PLACEMENT_VERSION = 2;
export const SECTION_TARGET = 150;   // Wörter und Wendungen je Abschnitt (ungefähr; Grenzen fallen auf Lektionsgrenzen)
export const MAX_SECTIONS = 16;      // größere Decks: größere Abschnitte statt mehr Proben
export const ITEMS = 4, EXTRA = 2;
export const maxProbes = K => Math.ceil(Math.log2(K + 1)) + 1;   // binäre Suche plus eine Probe für einen schlecht gewählten Einstieg
export const CHECKS_PER_DAY = 10;    // vorausgesetzte Wörter, die je Lerntag kurz geprüft werden
export const CHECK_ZONE = 150;       // so viele Wörter und Wendungen direkt vor dem Startpunkt werden geprüft (ganze Lektionen)

// Kann-Sätze. pos = ungefähr so viele Wörter und Wendungen kennt jemand, der das sagt: dort liegt die erste Probe (null = kein Test, ganz vorn).
// minUnits: Satz erscheint erst, wenn das Deck so viele Wörter und Wendungen hat (sonst könnte der Test es gar nicht bestätigen).
export const LEVELS = [
  { text: 'Ich fange bei null an.', pos: null },
  { text: 'Ich kenne ein paar Wörter und Sätze: grüßen, mich vorstellen, etwas bestellen.', pos: 1 },
  { text: 'Ich komme im Alltag zurecht: einkaufen, nach dem Weg fragen, von mir erzählen.', pos: 400 },
  { text: 'Ich kann mich über vieles unterhalten, auch über Vergangenes und Pläne.', pos: 1000 },
  { text: 'Ich verstehe die meisten Gespräche und Serien; mir fehlen nur seltenere Wörter.', pos: 2500, minUnits: 2000 },
];
// Kann-Sätze für dieses Deck, mit ihrer Nummer (i) für die Einstufung
export const levelsFor = content => LEVELS.map((l, i) => ({ ...l, i })).filter(l => !l.minUnits || (content.units || []).length >= l.minUnits);

// ---------- reproduzierbarer Zufall ----------
function hash(text) { let h = 2166136261; for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
export function rng(seed) {   // mulberry32
  let a = hash(String(seed));
  return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const shuffled = (list, r) => { const a = list.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

// ---------- Abschnitte ----------
const lessonNo = t => { const m = /^Lektion (\d+)$/.exec(t || ''); return m ? +m[1] : null; };
export function rangeTitle(first, last) {
  const a = lessonNo(first), b = lessonNo(last);
  if (a !== null && b !== null) return a === b ? `Lektion ${a}` : `Lektion ${a}–${b}`;
  return first === last ? first : `${first} bis ${last}`;
}
// Etwa SECTION_TARGET Wörter und Wendungen je Abschnitt, mindestens 3 Abschnitte (wenn es so viele Lektionen gibt), Grenzen auf Lektionsgrenzen
export function buildSections(content, target = SECTION_TARGET) {
  const units = content.units || [];
  if (!units.length) return [];
  const maxPos = Math.max(...units.map(u => u.pos));
  let blocks = (content.blocks || []).filter(b => b.to >= b.from).slice().sort((a, b) => a.from - b.from);
  if (!blocks.length) blocks = [{ title: 'Kurs', from: 1, to: maxPos }];
  const count = b => units.filter(u => u.pos >= b.from && u.pos <= b.to).length;
  const sizes = blocks.map(count), total = sizes.reduce((x, y) => x + y, 0);
  const K = Math.max(1, Math.min(blocks.length, MAX_SECTIONS, Math.max(3, Math.round(total / target))));
  const out = [];
  let cur = null, cum = 0;
  blocks.forEach((b, i) => {
    if (!cur) cur = { from: b.from, to: b.to, first: b.title, last: b.title, n: 0 };
    cur.to = b.to; cur.last = b.title; cur.n += sizes[i]; cum += sizes[i];
    const left = blocks.length - i - 1, need = K - out.length - 1;
    if (need > 0 && (cum >= (out.length + 1) * total / K || left === need)) { out.push(cur); cur = null; }
  });
  if (cur) out.push(cur);
  return out.map((s, i) => ({ i, from: s.from, to: s.to, n: s.n, title: rangeTitle(s.first, s.last) }));
}
// Prüfbereich: ganze Lektionen direkt vor dem Startpunkt, zusammen mindestens CHECK_ZONE Wörter und Wendungen. Rückgabe: erste Position.
export function checkZoneFrom(content, startPos, size = CHECK_ZONE) {
  if (startPos <= 1) return 1;
  const units = content.units || [];
  const blocks = (content.blocks || []).filter(b => b.to >= b.from && b.from < startPos).sort((a, b) => b.from - a.from);
  let n = 0;
  for (const b of blocks) {
    n += units.filter(u => u.pos >= b.from && u.pos <= b.to && u.pos < startPos).length;
    if (n >= size) return b.from;
  }
  return blocks.length ? blocks[blocks.length - 1].from : Math.max(1, startPos - size);   // weniger als CHECK_ZONE davor: alles; ohne Lektionen: Positionen
}

// ---------- Aufgaben ----------
const wordCount = t => String(t).split(/\s+/).filter(w => /\p{L}/u.test(w)).length;
const norm = t => String(t).toLowerCase().replace(/[^\p{L}\p{N} ]/gu, ' ').replace(/\s+/g, ' ').trim();
// Aufbau eines Satzes: Folge der Satzzeichen und Wortzahl je Teil. „Langsam! Du sprichst zu schnell, ich verstehe nicht.“ → „!,.“ mit 1, 4, 3.
// Falsche Bedeutungen haben denselben Aufbau, sonst verrät die Form die Lösung (Arne, 21.10.2026).
function shape(t) {
  const segs = [];
  let sk = '', cur = 0;
  for (const tok of String(t).split(/\s+/)) {
    if (/[\p{L}\p{N}]/u.test(tok)) cur++;
    const m = /[.,!?:;…–—]+$/u.exec(tok);
    if (m) { sk += m[0][0]; segs.push(cur); cur = 0; }
  }
  if (cur) { sk += '_'; segs.push(cur); }
  return { sk, segs };
}
export const MAX_SHAPE_DIFF = 2;   // höchstens so viele Wörter Unterschied, über alle Teile zusammen
const shapeDiff = (a, b) => a.segs.reduce((n, v, i) => n + Math.abs(v - b.segs[i]), 0);
// Satzangaben einmal je Inhalt vorbereiten (normalisierte Bedeutung, Wörter, Aufbau), damit die Auswahl auch in der Simulation schnell bleibt
const PREP = new WeakMap();
function prepared(content) {
  let P = PREP.get(content);
  if (!P) {
    const list = content.sentences.filter(s => s.it && s.de).map(s => {
      const de = norm(s.de), it = norm(s.it).split(' '), deWords = de.split(' ');
      return { s, de, deWords, content: new Set(deWords.filter(w => w.length >= 4)), itWords: new Set(it.filter(w => w.length >= 3)), itAll: new Set(it), first: it[0], ...shape(s.de) };
    });
    const bySk = {};
    for (const x of list) (bySk[x.sk] || (bySk[x.sk] = [])).push(x);
    P = { list, bySk };
    PREP.set(content, P);
  }
  return P;
}
// Kann nicht als falsche Bedeutung dienen: gleiche Bedeutung, oder die deutschen Sätze unterscheiden sich nur in Wörtern, die auch im Satz der
// Zielsprache stehen (Namen, Zahlen; dann prüft die Auswahl nur den Namen), oder sie teilen so viele Wörter, dass beide passen könnten.
function unusable(t, o) {
  if (o === t || o.de === t.de) return true;
  const rest = x => x.deWords.filter(w => w && !t.itAll.has(w)).sort().join(' ');
  if (rest(t) === rest(o)) return true;
  const a = new Set(t.deWords), b = new Set(o.deWords);
  let both = 0; a.forEach(w => { if (b.has(w)) both++; });
  return both / (a.size + b.size - both) >= 0.5;
}
// Falsche Bedeutungen mit gleichem Aufbau aus dem ganzen Kurs (die Zielsprache dieser Sätze sieht man nicht)
const sameShape = (P, t) => t.matches || (t.matches = (P.bySk[t.sk] || []).filter(d => shapeDiff(t, d) <= MAX_SHAPE_DIFF && !unusable(t, d)));   // je Satz einmal
// Schnelle Vorauswahl (nur Aufbau, ohne die teurere Ähnlichkeitsprüfung), damit auch Decks mit 5.000 Sätzen ohne Wartezeit laufen
const shapeCount = (P, t) => t.nShape ?? (t.nShape = (P.bySk[t.sk] || []).filter(d => d !== t && shapeDiff(t, d) <= MAX_SHAPE_DIFF).length);

// count Sätze aus dem Abschnitt, deren neuestes Wort darin liegt (maxPos); nur solche mit mindestens 5 möglichen falschen Bedeutungen gleichen
// Aufbaus; bevorzugt Hauptsätze mit mindestens 3 Wörtern, möglichst mit verschiedenen Lernzielen und verschiedenem Satzanfang.
// Zu jedem 3 falsche Bedeutungen gleichen Aufbaus: zuerst mit denselben Namen wie der Satz, dann mit möglichst vielen gemeinsamen Wörtern mit der
// richtigen Bedeutung (wer nur ein leichtes Wort erkennt, kann so nicht aussortieren), dann möglichst gleich lange Teile.
// Nie die richtige Bedeutung eines anderen Satzes derselben Probe.
export function itemsFor(content, sec, seed, count, used = new Set()) {
  const r = rng(seed);
  const P = prepared(content);
  const pool = P.list.filter(x => x.s.maxPos >= sec.from && x.s.maxPos <= sec.to && !used.has(x.s.id));
  const enough = x => shapeCount(P, x) >= 6 && sameShape(P, x).length >= 5;   // genaue Prüfung nur für Sätze, die die Vorauswahl bestehen
  const ranked = shuffled(pool, r)
    .map(x => ({ x, k: (shapeCount(P, x) >= 6 ? 0 : 4) + (wordCount(x.s.it) >= 3 ? 0 : 2) + (x.s.slot === 1 ? 0 : 1) }))
    .sort((a, b) => a.k - b.k).map(y => y.x);
  const chosen = [], seenDe = new Set();
  for (const strict of [true, false]) {
    for (const x of ranked) {
      if (chosen.length >= count) break;
      if (chosen.includes(x) || seenDe.has(x.de)) continue;
      if (strict && (chosen.some(c => c.s.unit === x.s.unit || c.first === x.first) || !enough(x))) continue;
      seenDe.add(x.de); chosen.push(x);
    }
  }
  return chosen.map(t => {
    const score = d => {
      let names = 0, shared = 0;
      d.deWords.forEach(w => { if (t.itWords.has(w)) names++; });
      d.content.forEach(w => { if (t.content.has(w)) shared++; });
      return { d, names, shared, diff: shapeDiff(t, d) };
    };
    const pick = (cands, n, have) => {
      const out = [];
      const sorted = shuffled(cands, r).map(score).sort((p, q) => (q.names - p.names) || (q.shared - p.shared) || (p.diff - q.diff)).map(y => y.d);
      for (const d of sorted) {
        if (out.length >= n) break;
        if (seenDe.has(d.de) || have.has(d.de) || out.some(o => o.de === d.de) || unusable(t, d)) continue;   // seenDe: alle richtigen Bedeutungen dieser Probe
        out.push(d);
      }
      return out;
    };
    let wrong = pick(sameShape(P, t), 3, new Set());
    // Notfall (sehr seltener Aufbau): gleiche Satzzeichen, dann irgendein Satz
    if (wrong.length < 3) wrong = wrong.concat(pick((P.bySk[t.sk] || []).filter(d => !wrong.includes(d)), 3 - wrong.length, new Set(wrong.map(d => d.de))));
    if (wrong.length < 3) wrong = wrong.concat(pick(P.list.filter(d => !wrong.includes(d)), 3 - wrong.length, new Set(wrong.map(d => d.de))));
    const options = shuffled([{ de: t.s.de, ok: true }, ...wrong.map(d => ({ de: d.s.de, ok: false }))], r);
    return { sid: t.s.id, it: t.s.it, audio: t.s.audio || null, options };
  });
}

// ---------- Ablauf (adaptiv) ----------
// Abschnitt der ersten Probe: der, in dem die Position des Kann-Satzes liegt (bei kleinerem Deck der letzte)
export function firstSection(level, sections) {
  const pos = LEVELS[level] ? LEVELS[level].pos || 1 : 1;
  const i = sections.findIndex(s => pos <= s.to);
  return i < 0 ? sections.length - 1 : i;
}

// Neue Einstufung. Ohne Test (Stufe 0 oder Kurs ohne Abschnitte) ist sie sofort fertig: Start ganz vorn.
export function createPlacement(content, { level = 1, seed = 1 } = {}) {
  const sections = buildSections(content);
  const st = { v: PLACEMENT_VERSION, seed: String(seed), level, sections, K: sections.length, lo: 0, hi: sections.length, probes: [], used: [], cur: null, done: false };
  if (!LEVELS[level] || LEVELS[level].pos === null || !st.K) return finish(st, content);
  openProbe(st, content, firstSection(level, sections));
  return st;
}
function openProbe(st, content, s) {
  const items = itemsFor(content, st.sections[s], `${st.seed}|${s}|${st.probes.length}`, ITEMS, new Set(st.used));
  items.forEach(x => st.used.push(x.sid));
  st.cur = { s, items, answers: [], extended: false };
  if (!items.length) settle(st, content, false);   // kein prüfbarer Satz im Abschnitt: vorsichtig als nicht bestanden werten
}
function settle(st, content, passed) {
  const cur = st.cur;
  st.probes.push({ s: cur.s, n: cur.items.length, c: cur.answers.filter(a => a === 1).length, passed, a: cur.items.map((x, i) => [x.sid, cur.answers[i] ?? null]) });
  if (passed) st.lo = Math.max(st.lo, cur.s + 1); else st.hi = Math.min(st.hi, cur.s);
  st.cur = null;
  if (st.lo >= st.hi || st.probes.length >= maxProbes(st.K)) return finish(st, content);
  openProbe(st, content, Math.floor((st.lo + st.hi) / 2));
  return st;
}
function finish(st, content) {
  st.done = true; st.cur = null;
  st.all = st.K > 0 && st.lo >= st.K;                       // alles bestanden: im letzten Abschnitt anfangen, damit es noch Neues gibt
  st.startSection = st.K ? Math.min(st.lo, st.K - 1) : 0;
  st.startPos = st.startSection > 0 ? st.sections[st.startSection].from : 1;
  st.checkFrom = checkZoneFrom(content, st.startPos);   // geprüft werden nur die Lektionen direkt vor dem Start
  return st;
}

// Aktuelle Aufgabe: { item, nr (1, 2, …), probe (1, 2, …), section } oder null, wenn fertig
export function current(st) {
  if (st.done || !st.cur) return null;
  const i = st.cur.answers.length;
  return { item: st.cur.items[i], nr: st.probes.reduce((n, p) => n + p.n, 0) + i + 1, probe: st.probes.length + 1, section: st.sections[st.cur.s] };
}

// Antwort: Index der gewählten Bedeutung oder -1 für „Weiß ich nicht“
export function respond(st, content, choice) {
  const cur = st.cur;
  if (st.done || !cur) return st;
  const it = cur.items[cur.answers.length];
  cur.answers.push(choice >= 0 && it.options[choice] && it.options[choice].ok ? 1 : choice < 0 ? -1 : 0);   // 1 richtig, 0 falsch, -1 weiß nicht
  if (cur.answers.length < cur.items.length) return st;
  const c = cur.answers.filter(a => a === 1).length, n = cur.items.length;
  if (!cur.extended && n === ITEMS && c === 2) {   // knapp: zwei Sätze mehr
    const more = itemsFor(content, st.sections[cur.s], `${st.seed}|${cur.s}|${st.probes.length}|mehr`, EXTRA, new Set(st.used));
    if (more.length) { more.forEach(x => st.used.push(x.sid)); cur.items.push(...more); cur.extended = true; return st; }
  }
  return settle(st, content, cur.extended ? c >= 4 : c >= Math.ceil(0.75 * n));
}

// Was im Profil gespeichert wird (deck_enrollments.placement): Ergebnis und Antworten, keine Satztexte
export const summary = (st, at, extra = {}) => ({ v: PLACEMENT_VERSION, level: st.level, seed: st.seed, K: st.K, startSection: st.startSection, startPos: st.startPos,
  suggestedPos: st.startPos, checkFrom: st.checkFrom, all: !!st.all, probes: st.probes, at, ...extra });

// Startpunkt aus dem Profil (frühere Einstufungen ohne v2 und „bei null loslegen“: ganz vorn)
export const startPosOf = profile => { const p = profile && profile.placement; return p && p.v === PLACEMENT_VERSION && p.startPos > 1 ? p.startPos : 1; };
// Ab hier wird geprüft (bis Startpunkt − 1). Einstufungen ohne checkFrom (gespeichert vor dieser Regel): aus den Lektionen berechnet.
export function checkFromOf(profile, content) {
  const start = startPosOf(profile);
  if (start <= 1) return 1;
  const p = profile.placement;
  return p.checkFrom >= 1 && p.checkFrom < start ? p.checkFrom : checkZoneFrom(content, start);
}

// Untertitel-Anteil der Wortformen, die in den vorausgesetzten Sätzen als eigenes Wort vorkommen (Wendungen zählen nicht; wie die Verständnis-Anzeige)
export function placedForms(content, startPos) {
  const U = Object.fromEntries(content.units.map(u => [u.id, u])), forms = new Set();
  if (startPos <= 1) return forms;
  for (const s of content.sentences) {
    if (!(s.maxPos < startPos)) continue;
    for (const [uid, sf] of Object.entries(s.surf || {})) if (U[uid] && U[uid].kind !== 'chunk' && U[uid].pos < startPos) forms.add(String(sf).toLowerCase());
  }
  return forms;
}
export const coverageOf = (content, forms) => { let c = 0; forms.forEach(f => { c += (content.formfreq || {})[f] || 0; }); return c; };
